// Enhanced and polished Projects component (React + MUI)
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  Chip,
  Typography,
  Grid,
  Card,
  CardContent,
  alpha,
  Fade,
  Skeleton,
  Stack,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getUserInfoFromToken } from "../../utils/Main";
import { baseUrl } from "../../constants";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";

const tokenHeader = () => {
  const t =
    localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
};

type SimpleUser = {
  id: number;
  username: string;
  name?: string | null;
  imageUrl?: string | null;
};

type ProjectManager = {
  userId: number;
  user?: {
    id: number;
    username: string;
    profile?: { name?: string | null; imageUrl?: string | null } | null;
  } | null;
};

interface Project {
  id: number;
  name: string;
  description?: string;
  deadline?: string;
  priority?: "low" | "medium" | "high";
  status?: "active" | "paused" | "completed";
  defaultVisibility?: "PRIVATE" | "DEPARTMENT" | "GENERAL" | "PROJECT";
  departmentId?: number;

  createdAt?: string;
  updatedAt?: string;
  archived?: boolean;
  startDate?: string;
  tags?: string[];

  department?: { id: number; name: string };

  userProjects?: {
    userId: number;
    projectId: number;
    user: {
      id: number;
      username: string;
      profile?: {
        name?: string | null;
        imageUrl?: string | null;
      } | null;
    } | null;
  }[];

  projectManagers?: ProjectManager[];
}

/** ------- Strongly typed API rows (fixes TS errors) ------- */
type ProjectUsersResponseRow = {
  user: {
    id: number;
    username: string;
    profile?: { name?: string | null; imageUrl?: string | null } | null;
  };
  projectId: number;
};

type ProjectManagersResponseRow = {
  userId: number;
  user?: {
    id: number;
    username: string;
    profile?: { name?: string | null; imageUrl?: string | null } | null;
  } | null;
};

const nameOf = (u?: SimpleUser | null) =>
  (u?.name && u.name.trim()) || u?.username || (u ? `User #${u.id}` : "");

const avatarOf = (u?: SimpleUser | null) =>
  u?.imageUrl || "/default-avatar.png";

const Projects: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const goToProjectDetails = (id: number) => navigate(`/projects/${id}`);

  // Who am I?
  const [userId, setUserId] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Admin-only view toggle
  const [viewMode, setViewMode] = useState<"own" | "all">("own");

  // For creating a project
  const [deadline, setDeadline] = useState<Dayjs | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [form, setForm] = useState<
    Partial<Project> & { departmentId?: number }
  >({
    name: "",
    description: "",
    deadline: "",
    priority: "medium",
    status: "active",
    defaultVisibility: "PRIVATE",
    departmentId: undefined,
  });

  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterVisibility, setFilterVisibility] = useState("");
  const [filterDepartmentId, setFilterDepartmentId] = useState<number | "">(
    ""
  );

  const [departments, setDepartments] = useState<
    { id: number; name: string }[]
  >([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editProjectId, setEditProjectId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);

  // === Managers state for EDIT dialog ===
  const [mgrOptions, setMgrOptions] = useState<SimpleUser[]>([]);
  const [mgrLoading, setMgrLoading] = useState(false);
  const [mgrValue, setMgrValue] = useState<SimpleUser[]>([]);
  const mgrSearchRef = useRef<number | undefined>(undefined);

  // Load current user info once
  useEffect(() => {
    const user = getUserInfoFromToken(); // should include { id, role }
    if (!user || !user.role || !localStorage.getItem("accessToken")) {
      navigate("/");
      return;
    }
    setUserId(Number(user.id));
    const admin = String(user.role).toLowerCase() === "admin";
    setIsAdmin(admin);
    setViewMode(admin ? "all" : "own");
  }, [navigate]);

  // Projects + departments
  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const { data } = await axios.get<Project[]>(
        `${baseUrl}/projects/user/projects`,
        {
          headers: tokenHeader(),
          params: isAdmin ? { scope: viewMode } : undefined,
        }
      );
      const sorted = (data || []).sort((a, b) => a.id - b.id);
      setProjects(sorted);
    } catch (err) {
      console.error("Failed to fetch projects", err);
      setSnackbar({
        open: true,
        message: "Failed to load projects",
        severity: "error",
      });
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data } = await axios.get<{ id: number; name: string }[]>(
        `${baseUrl}/departments`,
        { headers: tokenHeader() }
      );
      setDepartments(data);
    } catch (err) {
      console.error("Failed to load departments", err);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when admin toggles scope
  useEffect(() => {
    if (isAdmin) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, isAdmin]);

  const handleInputChange = (
    field: keyof (Project & { departmentId?: number }),
    value: any
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateProject = async () => {
    try {
      setLoading(true);
      await axios.post<Project>(
        `${baseUrl}/projects`,
        {
          ...form,
          // coerce departmentId to number or undefined
          departmentId:
            form.departmentId !== undefined && form.departmentId !== null
              ? Number(form.departmentId)
              : undefined,
          deadline: deadline ? deadline.toISOString() : null,
        },
        { headers: tokenHeader() }
      );

      setSnackbar({
        open: true,
        message: "Project created!",
        severity: "success",
      });
      setForm({
        name: "",
        description: "",
        deadline: "",
        priority: "medium",
        status: "active",
        defaultVisibility: "PRIVATE",
        departmentId: undefined,
      });
      setDeadline(null);
      fetchProjects();
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: "Failed to create project",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /** ===== Strongly-typed mappers (no `{}` placeholders) ===== */
  const mapProjectUsersToOptions = (
    rows: ProjectUsersResponseRow[]
  ): SimpleUser[] =>
    (rows || []).map((r) => ({
      id: r.user.id,
      username: r.user.username,
      name: r.user.profile?.name ?? null,
      imageUrl: r.user.profile?.imageUrl ?? null,
    }));

  const fetchEligibleManagerOptions = async (
    projectId: number,
    q?: string
  ) => {
    setMgrLoading(true);
    try {
      const { data } = await axios.get<ProjectUsersResponseRow[]>(
        `${baseUrl}/projects/project-users/${projectId}`,
        {
          headers: tokenHeader(),
          params: q?.trim() ? { name: q.trim() } : undefined,
        }
      );
      setMgrOptions(mapProjectUsersToOptions(data));
    } catch (e) {
      console.error("Failed to load project users", e);
      setMgrOptions([]);
    } finally {
      setMgrLoading(false);
    }
  };

  const fetchCurrentManagers = async (projectId: number) => {
    try {
      const { data } = await axios.get<ProjectManagersResponseRow[]>(
        `${baseUrl}/projects/${projectId}/managers`,
        { headers: tokenHeader() }
      );
      const mapped: SimpleUser[] = (data || []).map((m) => ({
        id: m.user?.id ?? m.userId,
        username: m.user?.username ?? `user_${m.userId}`,
        name: m.user?.profile?.name ?? null,
        imageUrl: m.user?.profile?.imageUrl ?? null,
      }));
      setMgrValue(mapped);
    } catch (e) {
      console.error("Failed to load managers", e);
      setMgrValue([]);
    }
  };

  const openEdit = async (project: Project) => {
    setForm(project);
    setEditProjectId(project.id);
    setEditDialogOpen(true);
    await Promise.all([
      fetchCurrentManagers(project.id),
      fetchEligibleManagerOptions(project.id),
    ]);
  };

  const handleUpdateProject = async () => {
    if (!editProjectId) return;

    try {
      setLoading(true);

      const {
        id,
        createdAt,
        updatedAt,
        archived,
        startDate,
        tags,
        department,
        userProjects,
        projectManagers,
        ...cleanForm
      } = form;

      // 1) update core fields
      await axios.patch(
        `${baseUrl}/projects/${editProjectId}`,
        {
          ...cleanForm,
          departmentId:
            cleanForm.departmentId !== undefined &&
            cleanForm.departmentId !== null
              ? Number(cleanForm.departmentId)
              : undefined,
          deadline: form.deadline
            ? dayjs(form.deadline).toISOString()
            : undefined,
        },
        { headers: tokenHeader() }
      );

      // 2) update managers selection
      const userIds = (mgrValue || []).map((u) => u.id);
      await axios.patch(
        `${baseUrl}/projects/${editProjectId}/managers`,
        { userIds },
        { headers: tokenHeader() }
      );

      setSnackbar({
        open: true,
        message: "Project updated!",
        severity: "success",
      });
      setEditDialogOpen(false);
      fetchProjects();
    } catch (err: any) {
      console.error("❌ Update failed:", err?.response?.data || err.message);
      setSnackbar({
        open: true,
        message: "Failed to update project",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!deleteProjectId) return;
    try {
      await axios.delete(`${baseUrl}/projects/${deleteProjectId}`, {
        headers: tokenHeader(),
      });
      setSnackbar({
        open: true,
        message: "Project deleted!",
        severity: "success",
      });
      fetchProjects();
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to delete project",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // ---------- Visible list (admin toggle + filters) ----------
  const visibleProjects = useMemo(() => {
    let list = projects;

    if (!isAdmin || viewMode === "own") {
      if (userId != null) {
        list = list.filter((p) =>
          (p.userProjects || []).some((up) => up?.userId === userId)
        );
      } else {
        list = [];
      }
    }

    list = list
      .filter((p) =>
        searchText
          ? p.name.toLowerCase().includes(searchText.toLowerCase())
          : true
      )
      .filter((p) => (filterPriority ? p.priority === filterPriority : true))
      .filter((p) => (filterStatus ? p.status === filterStatus : true))
      .filter((p) =>
        filterVisibility ? p.defaultVisibility === filterVisibility : true
      )
      .filter((p) =>
        filterDepartmentId ? p.departmentId === filterDepartmentId : true
      );

    return list;
  }, [
    projects,
    isAdmin,
    viewMode,
    userId,
    searchText,
    filterPriority,
    filterStatus,
    filterVisibility,
    filterDepartmentId,
  ]);

  // Helper functions for styling
  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case "high":
        return theme.palette.error.main;
      case "medium":
        return theme.palette.warning.main;
      case "low":
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "active":
        return theme.palette.success.main;
      case "paused":
        return theme.palette.warning.main;
      case "completed":
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getVisibilityColor = (visibility?: string) => {
    switch (visibility) {
      case "PRIVATE":
        return theme.palette.error.main;
      case "DEPARTMENT":
        return theme.palette.warning.main;
      case "GENERAL":
        return theme.palette.info.main;
      case "PROJECT":
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const hasActiveFilters = searchText || filterPriority || filterStatus || filterVisibility || filterDepartmentId;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "100vh" }}>
        {/* Enhanced Header Section */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 4,
              background: theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.98)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backdropFilter: "blur(20px)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h4" fontWeight={900} sx={{ mb: 0.5 }}>
                  Projects
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage and track all your projects
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddDialogOpen(true);
                  setForm({
                    name: "",
                    description: "",
                    deadline: "",
                    priority: "medium",
                    status: "active",
                    defaultVisibility: "PRIVATE",
                    departmentId: undefined,
                  });
                  setDeadline(null);
                }}
                sx={{
                  minWidth: 160,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  "&:hover": {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                New Project
              </Button>
            </Stack>

            {/* Enhanced Filters */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                pt: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <TextField
                label="Search Projects"
                variant="outlined"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                  },
                }}
              />

              {isAdmin && (
                <Select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as "own" | "all")}
                  size="small"
                  sx={{
                    minWidth: 140,
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="own">My Projects</MenuItem>
                  <MenuItem value="all">All Projects</MenuItem>
                </Select>
              )}

              <Select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                displayEmpty
                size="small"
                sx={{ minWidth: 140, borderRadius: 2 }}
              >
                <MenuItem value="">All Priorities</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>

              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                displayEmpty
                size="small"
                sx={{ minWidth: 140, borderRadius: 2 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>

              <Select
                value={filterVisibility}
                onChange={(e) => setFilterVisibility(e.target.value)}
                displayEmpty
                size="small"
                sx={{ minWidth: 150, borderRadius: 2 }}
              >
                <MenuItem value="">All Visibility</MenuItem>
                <MenuItem value="PRIVATE">Private</MenuItem>
                <MenuItem value="DEPARTMENT">Department</MenuItem>
                <MenuItem value="GENERAL">General</MenuItem>
                <MenuItem value="PROJECT">Project</MenuItem>
              </Select>

              <Select
                value={filterDepartmentId}
                onChange={(e) =>
                  setFilterDepartmentId(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                displayEmpty
                size="small"
                sx={{ minWidth: 180, borderRadius: 2 }}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setSearchText("");
                    setFilterPriority("");
                    setFilterStatus("");
                    setFilterVisibility("");
                    setFilterDepartmentId("");
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Paper>
        </Fade>

        {/* Projects Grid */}
        {projectsLoading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    background: theme.palette.mode === "dark"
                      ? alpha(theme.palette.grey[900], 0.5)
                      : alpha(theme.palette.grey[50], 0.8),
                  }}
                >
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                      <Skeleton variant="rounded" width={80} height={24} />
                      <Skeleton variant="rounded" width={80} height={24} />
                    </Box>
                    <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : visibleProjects.length === 0 ? (
          <Fade in timeout={800}>
            <Paper
              elevation={0}
              sx={{
                p: 8,
                textAlign: "center",
                borderRadius: 4,
                background: theme.palette.mode === "dark"
                  ? alpha(theme.palette.grey[900], 0.5)
                  : alpha(theme.palette.grey[50], 0.8),
                border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
              }}
            >
              <BusinessIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No projects found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "Create your first project to get started"}
              </Typography>
              {!hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddDialogOpen(true)}
                  sx={{ borderRadius: 2 }}
                >
                  Create Project
                </Button>
              )}
            </Paper>
          </Fade>
        ) : (
          <Grid container spacing={3}>
            {visibleProjects.map((p, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                <Fade in timeout={600 + index * 100}>
                  <Card
                    elevation={0}
                    onClick={() => goToProjectDetails(p.id)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.98)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${getPriorityColor(p.priority)}, ${getStatusColor(p.status)})`,
                      },
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: theme.palette.mode === "dark"
                          ? `0 12px 40px ${alpha(theme.palette.common.black, 0.4)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                          : `0 12px 40px ${alpha(theme.palette.common.black, 0.1)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.15)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    <CardContent sx={{ flex: 1, pb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {p.name}
                          </Typography>
                          {p.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mb: 1.5,
                              }}
                            >
                              {p.description}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            ml: 1,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(p);
                              }}
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteProjectId(p.id);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Stack>

                      <Stack spacing={1.5}>
                        {/* Status and Priority Chips */}
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Chip
                            label={p.status || "unknown"}
                            size="small"
                            sx={{
                              bgcolor: alpha(getStatusColor(p.status), 0.15),
                              color: getStatusColor(p.status),
                              fontWeight: 600,
                              border: `1px solid ${alpha(getStatusColor(p.status), 0.3)}`,
                            }}
                          />
                          <Chip
                            label={p.priority || "medium"}
                            size="small"
                            sx={{
                              bgcolor: alpha(getPriorityColor(p.priority), 0.15),
                              color: getPriorityColor(p.priority),
                              fontWeight: 600,
                              border: `1px solid ${alpha(getPriorityColor(p.priority), 0.3)}`,
                            }}
                          />
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        {/* Project Details */}
                        <Stack spacing={1}>
                          {p.deadline && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                {dayjs(p.deadline).format("MMM DD, YYYY")}
                              </Typography>
                            </Box>
                          )}

                          {p.department && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <ApartmentIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                {p.department.name}
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <VisibilityIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Chip
                              label={p.defaultVisibility || "PRIVATE"}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                bgcolor: alpha(getVisibilityColor(p.defaultVisibility), 0.15),
                                color: getVisibilityColor(p.defaultVisibility),
                                border: `1px solid ${alpha(getVisibilityColor(p.defaultVisibility), 0.3)}`,
                              }}
                            />
                          </Box>
                        </Stack>

                        <Divider sx={{ my: 0.5 }} />

                        {/* Managers and Users */}
                        <Box>
                          {p.projectManagers && p.projectManagers.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                                Managers
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {p.projectManagers.slice(0, 3).map((m) => {
                                  const u = m.user;
                                  const name = u?.profile?.name || u?.username || `User #${m.userId}`;
                                  const imageUrl = u?.profile?.imageUrl;
                                  return (
                                    <Tooltip key={m.userId} title={name}>
                                      <Avatar
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          border: `2px solid ${theme.palette.background.paper}`,
                                        }}
                                        src={imageUrl || "/default-avatar.png"}
                                        alt={name}
                                      />
                                    </Tooltip>
                                  );
                                })}
                                {p.projectManagers.length > 3 && (
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main,
                                      fontSize: "0.7rem",
                                      fontWeight: 700,
                                    }}
                                  >
                                    +{p.projectManagers.length - 3}
                                  </Avatar>
                                )}
                              </Box>
                            </Box>
                          )}

                          {p.userProjects && p.userProjects.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                                Team Members ({p.userProjects.length})
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {p.userProjects.slice(0, 5).map((up) => {
                                  const name = up?.user?.profile?.name || up?.user?.username || "Unnamed";
                                  const imageUrl = up?.user?.profile?.imageUrl;
                                  return (
                                    <Tooltip key={up?.userId} title={name}>
                                      <Avatar
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          border: `2px solid ${theme.palette.background.paper}`,
                                        }}
                                        src={imageUrl || "/default-avatar.png"}
                                        alt={name}
                                      />
                                    </Tooltip>
                                  );
                                })}
                                {p.userProjects.length > 5 && (
                                  <Avatar
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                      color: theme.palette.secondary.main,
                                      fontSize: "0.65rem",
                                      fontWeight: 700,
                                    }}
                                  >
                                    +{p.userProjects.length - 5}
                                  </Avatar>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add Project */}
        <Dialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.98)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h5" fontWeight={700}>
              Create New Project
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Add a new project to your workspace
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 2 }}
          >
            <TextField
              label="Name"
              value={form.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
            />

            <Select
              value={form.departmentId ?? ""}
              onChange={(e) =>
                handleInputChange(
                  "departmentId",
                  e.target.value === "" ? undefined : Number(e.target.value)
                )
              }
              fullWidth
            >
              <MenuItem value="">Select Department</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>

            <DatePicker
              label="Deadline"
              value={deadline}
              onChange={(newValue) => {
                setDeadline(newValue);
                handleInputChange(
                  "deadline",
                  newValue ? newValue.toISOString() : null
                );
              }}
              disablePast
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
            />

            <Select
              value={form.priority || "medium"}
              onChange={(e) => handleInputChange("priority", e.target.value)}
              fullWidth
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
            <Select
              value={form.status || "active"}
              onChange={(e) => handleInputChange("status", e.target.value)}
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
            <Select
              value={form.defaultVisibility || "PRIVATE"}
              onChange={(e) =>
                handleInputChange("defaultVisibility", e.target.value)
              }
              fullWidth
            >
              <MenuItem value="PRIVATE">Private</MenuItem>
              <MenuItem value="DEPARTMENT">Department</MenuItem>
              <MenuItem value="GENERAL">General</MenuItem>
              <MenuItem value="PROJECT">Project</MenuItem>
            </Select>

            {/* Note about managers in Add flow */}
            <Alert severity="info">
              Managers can be assigned after users are added to the project
              (Edit the project to set managers).
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button
              onClick={() => setAddDialogOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                await handleCreateProject();
                setAddDialogOpen(false);
              }}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 100 }}
            >
              {loading ? <CircularProgress size={20} /> : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Project */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.98)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h5" fontWeight={700}>
              Edit Project
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Update project details and settings
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 2 }}
          >
            <TextField
              label="Name"
              value={form.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
            />

            <Select
              value={form.departmentId ?? ""}
              onChange={(e) =>
                handleInputChange(
                  "departmentId",
                  e.target.value === "" ? undefined : Number(e.target.value)
                )
              }
              fullWidth
            >
              <MenuItem value="">Select Department</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>

            <DatePicker
              label="Deadline"
              value={form.deadline ? dayjs(form.deadline) : null}
              onChange={(newValue) => {
                setForm((prev) => ({
                  ...prev,
                  deadline: newValue ? newValue.toISOString() : undefined,
                }));
              }}
              disablePast
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
            />

            <Select
              value={form.priority || "medium"}
              onChange={(e) => handleInputChange("priority", e.target.value)}
              fullWidth
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
            <Select
              value={form.status || "active"}
              onChange={(e) => handleInputChange("status", e.target.value)}
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
            <Select
              value={form.defaultVisibility || "PRIVATE"}
              onChange={(e) =>
                handleInputChange("defaultVisibility", e.target.value)
              }
              fullWidth
            >
              <MenuItem value="PRIVATE">Private</MenuItem>
              <MenuItem value="DEPARTMENT">Department</MenuItem>
              <MenuItem value="GENERAL">General</MenuItem>
              <MenuItem value="PROJECT">Project</MenuItem>
            </Select>

            {/* NEW: Project Managers (multi) */}
            <Autocomplete
              multiple
              options={mgrOptions}
              loading={mgrLoading}
              value={mgrValue}
              onChange={(_, val) => setMgrValue(val || [])}
              onInputChange={(_, val) => {
                if (mgrSearchRef.current)
                  window.clearTimeout(mgrSearchRef.current);
                mgrSearchRef.current = window.setTimeout(() => {
                  if (editProjectId)
                    fetchEligibleManagerOptions(editProjectId, val);
                }, 300);
              }}
              getOptionLabel={(o) => nameOf(o)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterSelectedOptions
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={avatarOf(option)}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Box>
                      <Box>{nameOf(option)}</Box>
                      <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                        @{option.username}
                      </Box>
                    </Box>
                  </Box>
                </li>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={nameOf(option)}
                    avatar={<Avatar src={avatarOf(option)} />}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Managers"
                  placeholder="Search project users…"
                />
              )}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateProject}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 100 }}
            >
              {loading ? <CircularProgress size={20} /> : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Project */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.98)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h5" fontWeight={700} color="error">
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete this project? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteProject}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() =>
            setSnackbar((prev) => ({ ...prev, open: false }))
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() =>
              setSnackbar((prev) => ({ ...prev, open: false }))
            }
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Projects;
